import React from 'react';
import { ChevronLeft, ChevronRight, Check, Clock, XCircle } from 'lucide-react';

// Utilidades
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d) => {
  const c = new Date(d);
  const day = c.getDay(); // 0..6 (dom..sab)
  const delta = (day + 6) % 7; // lunes como inicio
  c.setDate(c.getDate() - delta);
  c.setHours(0, 0, 0, 0);
  return c;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const fmtYMD = (d) => d.toISOString().slice(0, 10);

export default function AgendaCalendar({
  monthDate,
  onMonthChange,
  selectedDate,
  onSelectDate,
  statsByDay,
  estadoFilter = "activas",
}) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = addDays(startOfWeek(addDays(monthEnd, 7)), -1);

  const weeks = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
    const ymd = fmtYMD(d);
    const sameMonth = d.getMonth() === monthDate.getMonth();
    const s = statsByDay[ymd] || { aprobadas: 0, pendientes: 0, canceladas: 0, rechazadas: 0 };
    const isSelected = selectedDate === ymd;

    let cls =
      'rounded-xl p-2 text-sm border transition-all duration-150 cursor-pointer hover:shadow-sm ';

    if (isSelected) {
      cls += 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200 text-indigo-900 ';
    } else {
      // Prioridades según filtro
      if (estadoFilter === "canceladas") {
        if (s.rechazadas > 0) cls += 'border-transparent bg-red-50 text-red-900 ';
        else if (s.canceladas > 0) cls += 'border-transparent bg-zinc-100 text-zinc-800 ';
        else cls += 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 ';
      } else if (estadoFilter === "todas") {
        if (s.rechazadas > 0) cls += 'border-transparent bg-red-50 text-red-900 ';
        else if (s.canceladas > 0) cls += 'border-transparent bg-zinc-100 text-zinc-800 ';
        else if (s.aprobadas > 0) cls += 'border-transparent bg-emerald-50 text-emerald-900 ';
        else if (s.pendientes > 0) cls += 'border-transparent bg-amber-50 text-amber-900 ';
        else cls += 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 ';
      } else {
        // activas
        if (s.aprobadas > 0) cls += 'border-transparent bg-emerald-50 text-emerald-900 ';
        else if (s.pendientes > 0) cls += 'border-transparent bg-amber-50 text-amber-900 ';
        else cls += 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 ';
      }
    }

    weeks.push(
      <button
        key={ymd}
        onClick={() => onSelectDate(ymd)}
        disabled={!sameMonth}
        className={cls}
      >
        <div
          className={`text-xs font-medium ${
            sameMonth ? 'text-zinc-800' : 'text-zinc-400'
          }`}
        >
          {d.getDate()}
        </div>

        {(() => {
          const showActivas = estadoFilter === "activas";
          const showCanceladas = estadoFilter === "canceladas";
          const showTodas = estadoFilter === "todas";

          const pills = [];

          if ((showActivas || showTodas) && s.aprobadas > 0) {
            pills.push(
              <span
                key="aprobadas"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5"
              >
                <Check className="h-3 w-3" /> {s.aprobadas}
              </span>
            );
          }

          if ((showActivas || showTodas) && s.pendientes > 0) {
            pills.push(
              <span
                key="pendientes"
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5"
              >
                <Clock className="h-3 w-3" /> {s.pendientes}
              </span>
            );
          }

          if ((showCanceladas || showTodas) && s.canceladas > 0) {
            pills.push(
              <span
                key="canceladas"
                className="inline-flex items-center gap-1 rounded-full bg-zinc-200 text-zinc-800 px-1.5 py-0.5"
              >
                <XCircle className="h-3 w-3" /> {s.canceladas}
              </span>
            );
          }

          if ((showCanceladas || showTodas) && s.rechazadas > 0) {
            pills.push(
              <span
                key="rechazadas"
                className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-1.5 py-0.5"
              >
                <XCircle className="h-3 w-3" /> {s.rechazadas}
              </span>
            );
          }

          if (pills.length === 0) return null;

          return (
            <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
              {pills}
            </div>
          );
        })()}
      </button>
    );
  }

  const prevMonth = () =>
    onMonthChange(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1));
  const nextMonth = () =>
    onMonthChange(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1));

  const header = new Intl.DateTimeFormat('es-CL', {
    month: 'long',
    year: 'numeric'
  }).format(monthDate);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-md p-4">
      {/* Encabezado */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={prevMonth}
          className="rounded-lg border border-zinc-300 p-2 hover:bg-zinc-100 transition"
          title="Mes anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <h3 className="font-semibold text-zinc-800 capitalize tracking-tight">
          {header}
        </h3>

        <button
          onClick={nextMonth}
          className="rounded-lg border border-zinc-300 p-2 hover:bg-zinc-100 transition"
          title="Mes siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Días de la semana */}
      <div className="grid grid-cols-7 gap-2 text-[12px] font-medium text-zinc-500 mb-2 text-center">
        <div>Lun</div>
        <div>Mar</div>
        <div>Mié</div>
        <div>Jue</div>
        <div>Vie</div>
        <div>Sáb</div>
        <div>Dom</div>
      </div>

      {/* Grilla de días */}
      <div className="grid grid-cols-7 gap-2 text-center">{weeks}</div>
    </div>
  );
}
